"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.googleAdsListCampaigns = exports.googleAdsListCampaignsArgsSchema = void 0;
var zod_1 = require("zod");
var common_1 = require("./common");
// ---------- List Campaigns ----------
var listCampaignsParamsSchema = zod_1.z
    .object({
    customerId: zod_1.z
        .string()
        .nullish()
        .describe('The Google Ads customer ID to list campaigns from. If not provided, will list campaigns from all accessible accounts.'),
    includeRemoved: zod_1.z
        .boolean()
        .default(false)
        .describe('Include removed/deleted campaigns in the results. Defaults to false.'),
    campaignTypes: zod_1.z
        .array(zod_1.z.enum([
        'SEARCH',
        'DISPLAY',
        'VIDEO',
        'SHOPPING',
        'PERFORMANCE_MAX',
        'SMART',
        'HOTEL',
        'LOCAL',
        'DISCOVERY',
    ]))
        .nullish()
        .describe('Filter by specific campaign types. If not provided, returns all types.'),
    maxItems: zod_1.z
        .number()
        .positive()
        .default(1000)
        .describe('Maximum number of items to return. Defaults to 1000 to ensure all resources are listed. This is higher than performance tools (which default to 100) because list resources queries are structural (lighter weight) and completeness is prioritized over performance.'),
    failIfEmpty: zod_1.z
        .boolean()
        .default(false)
        .describe('Fail if no campaigns are found. Defaults to false.'),
})
    .describe('List all campaigns from Google Ads accounts');
var listCampaignsResponseSchema = zod_1.z.array(zod_1.z.object({
    campaignId: zod_1.z.string().describe('The campaign ID'),
    campaignName: zod_1.z.string().describe('The campaign name'),
    customerId: zod_1.z.string().describe('The customer ID this campaign belongs to'),
    customerName: zod_1.z.string().nullish().describe('The customer account name'),
    status: zod_1.z
        .enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED'])
        .describe('Campaign status'),
    campaignType: zod_1.z.string().describe('The type of campaign (SEARCH, DISPLAY, etc.)'),
    biddingStrategy: zod_1.z.string().nullish().describe('The bidding strategy type'),
    budget: zod_1.z
        .object({
        budgetId: zod_1.z.string().nullish(),
        name: zod_1.z.string().nullish(),
        amount: zod_1.z.number().nullish(),
    })
        .nullish()
        .describe('Budget information'),
    startDate: zod_1.z.string().nullish().describe('Campaign start date'),
    endDate: zod_1.z.string().nullish().describe('Campaign end date'),
    servingStatus: zod_1.z.string().nullish().describe('Current serving status'),
    adServingOptimizationStatus: zod_1.z.string().nullish().describe('Ad serving optimization status'),
    trackingUrlTemplate: zod_1.z.string().nullish().describe('Tracking URL template'),
    finalUrlSuffix: zod_1.z.string().nullish().describe('Final URL suffix'),
    adGroupIds: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('List of ad group IDs in this campaign (for non-Performance Max campaigns)'),
    assetGroupIds: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('List of asset group IDs in this campaign (for Performance Max campaigns)'),
}));
// ---------- List Ad Groups (Internal Response Schema) ----------
var listAdGroupsResponseSchema = zod_1.z.array(zod_1.z.object({
    adGroupId: zod_1.z.string().describe('The ad group ID'),
    adGroupName: zod_1.z.string().describe('The ad group name'),
    campaignId: zod_1.z.string().describe('The campaign ID this ad group belongs to'),
    campaignName: zod_1.z.string().describe('The campaign name'),
    customerId: zod_1.z.string().describe('The customer ID'),
    status: zod_1.z
        .enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED'])
        .describe('Ad group status'),
    adGroupType: zod_1.z.string().nullish().describe('The type of ad group'),
    cpcBidMicros: zod_1.z.number().nullish().describe('CPC bid in micros'),
    targetCpaMicros: zod_1.z.number().nullish().describe('Target CPA in micros'),
    targetRoas: zod_1.z.number().nullish().describe('Target ROAS (Return on Ad Spend)'),
    percentCpcBidMicros: zod_1.z.number().nullish().describe('Percent CPC bid in micros'),
    effectiveTargetCpaMicros: zod_1.z
        .number()
        .nullish()
        .describe('Effective target CPA in micros (inherited or set)'),
    effectiveTargetRoas: zod_1.z.number().nullish().describe('Effective target ROAS (inherited or set)'),
    finalUrlSuffix: zod_1.z.string().nullish().describe('Final URL suffix'),
    trackingUrlTemplate: zod_1.z.string().nullish().describe('Tracking URL template'),
    adIds: zod_1.z.array(zod_1.z.string()).nullish().describe('List of ad IDs in this ad group'),
}));
// ---------- List Asset Groups (Internal Response Schema) ----------
var listAssetGroupsResponseSchema = zod_1.z.array(zod_1.z.object({
    assetGroupId: zod_1.z.string().describe('The asset group ID'),
    assetGroupName: zod_1.z.string().describe('The asset group name'),
    campaignId: zod_1.z.string().describe('The campaign ID this asset group belongs to'),
    campaignName: zod_1.z.string().describe('The campaign name'),
    customerId: zod_1.z.string().describe('The customer ID'),
    status: zod_1.z
        .enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED'])
        .describe('Asset group status'),
    finalUrls: zod_1.z.array(zod_1.z.string()).nullish().describe('Final URLs for the asset group'),
    finalMobileUrls: zod_1.z
        .array(zod_1.z.string())
        .nullish()
        .describe('Final mobile URLs for the asset group'),
    path1: zod_1.z.string().nullish().describe('Path 1 for display URLs'),
    path2: zod_1.z.string().nullish().describe('Path 2 for display URLs'),
    adStrength: zod_1.z
        .string()
        .nullish()
        .describe('Ad strength rating (UNSPECIFIED, UNKNOWN, PENDING, NO_ADS, POOR, AVERAGE, GOOD, EXCELLENT)'),
    finalUrlSuffix: zod_1.z.string().nullish().describe('Final URL suffix'),
    adIds: zod_1.z.array(zod_1.z.string()).nullish().describe('List of ad IDs in this asset group'),
}));
// ---------- List Groups (Dynamic) ----------
var listGroupsParamsSchema = zod_1.z
    .object({
    customerId: zod_1.z.string().describe('The Google Ads customer ID'),
    campaignId: zod_1.z
        .string()
        .describe('The campaign ID to list groups from. Will automatically return ad groups for regular campaigns or asset groups for Performance Max campaigns.'),
    includeRemoved: zod_1.z
        .boolean()
        .default(false)
        .describe('Include removed/deleted groups in the results. Defaults to false.'),
    maxItems: zod_1.z
        .number()
        .positive()
        .default(1000)
        .describe('Maximum number of items to return. Defaults to 1000 to ensure all resources are listed. This is higher than performance tools (which default to 100) because list resources queries are structural (lighter weight) and completeness is prioritized over performance.'),
    failIfEmpty: zod_1.z
        .boolean()
        .default(false)
        .describe('Fail if no groups are found. Defaults to false.'),
})
    .describe('CRITICAL: This is the ONLY action for listing groups. Use "listGroups" - NOT "listAdGroups" or "listAssetGroups" (which do not exist). Automatically returns ad groups for regular campaigns, asset groups for Performance Max campaigns based on campaign type.');
var listGroupsResponseSchema = zod_1.z.union([
    listAdGroupsResponseSchema,
    listAssetGroupsResponseSchema,
]);
// ---------- List Ads ----------
var listAdsParamsSchema = zod_1.z
    .object({
    customerId: zod_1.z.string().describe('The Google Ads customer ID'),
    parentId: zod_1.z
        .string()
        .describe('The parent ID: ad group ID for search/display campaigns, or asset group ID for Performance Max campaigns'),
    parentType: zod_1.z
        .enum(['adGroup', 'assetGroup'])
        .describe('Type of parent: "adGroup" for search/display campaigns, "assetGroup" for Performance Max campaigns'),
    includeRemoved: zod_1.z
        .boolean()
        .default(false)
        .describe('Include removed/deleted ads in the results. Defaults to false.'),
    maxItems: zod_1.z
        .number()
        .positive()
        .default(1000)
        .describe('Maximum number of items to return. Defaults to 1000 to ensure all resources are listed. This is higher than performance tools (which default to 100) because list resources queries are structural (lighter weight) and completeness is prioritized over performance.'),
    failIfEmpty: zod_1.z
        .boolean()
        .default(false)
        .describe('Fail if no ads are found. Defaults to false.'),
})
    .describe('List all ads from an ad group or asset group');
// ---------- List Keywords ----------
var listKeywordsParamsSchema = zod_1.z
    .object({
    customerId: zod_1.z.string().describe('The Google Ads customer ID'),
    adGroupId: zod_1.z
        .string()
        .describe('The ad group ID to list keywords from (keywords only exist in ad groups, not asset groups)'),
    includeRemoved: zod_1.z
        .boolean()
        .default(false)
        .describe('Include removed/deleted keywords in the results. Defaults to false.'),
    maxItems: zod_1.z
        .number()
        .positive()
        .default(1000)
        .describe('Maximum number of items to return. Defaults to 1000 to ensure all resources are listed. This is higher than performance tools (which default to 100) because list resources queries are structural (lighter weight) and completeness is prioritized over performance.'),
    failIfEmpty: zod_1.z
        .boolean()
        .default(false)
        .describe('Fail if no keywords are found. Defaults to false.'),
})
    .describe('List all keywords from an ad group');
var listKeywordsResponseSchema = zod_1.z.array(zod_1.z.object({
    criterionId: zod_1.z.string().describe('The criterion ID (needed to construct resource name)'),
    keyword: zod_1.z.string().describe('The keyword text'),
    matchType: zod_1.z
        .enum(['BROAD', 'PHRASE', 'EXACT'])
        .nullish()
        .describe('The match type of the keyword'),
    adGroupId: zod_1.z.string().describe('The ad group ID'),
    adGroupName: zod_1.z.string().nullish().describe('The ad group name'),
    campaignId: zod_1.z.string().describe('The campaign ID'),
    campaignName: zod_1.z.string().nullish().describe('The campaign name'),
    customerId: zod_1.z.string().describe('The customer ID'),
    status: zod_1.z
        .enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED'])
        .describe('Keyword status'),
    cpcBidMicros: zod_1.z.number().nullish().describe('CPC bid in micros (if set)'),
    negative: zod_1.z.boolean().nullish().describe('Whether this is a negative keyword'),
}));
var listAdsResponseSchema = zod_1.z.array(zod_1.z.object({
    adId: zod_1.z.string().describe('The ad ID'),
    adName: zod_1.z.string().nullish().describe('The ad name (if set)'),
    adGroupId: zod_1.z.string().nullish().describe('The ad group ID (for search/display ads)'),
    adGroupName: zod_1.z.string().nullish().describe('The ad group name (for search/display ads)'),
    assetGroupId: zod_1.z.string().nullish().describe('The asset group ID (for Performance Max ads)'),
    assetGroupName: zod_1.z.string().nullish().describe('The asset group name (for Performance Max ads)'),
    campaignId: zod_1.z.string().nullish().describe('The campaign ID'),
    campaignName: zod_1.z.string().nullish().describe('The campaign name'),
    customerId: zod_1.z.string().describe('The customer ID'),
    status: zod_1.z
        .enum(['UNKNOWN', 'UNSPECIFIED', 'ENABLED', 'PAUSED', 'REMOVED'])
        .describe('Ad status'),
    adType: zod_1.z.string().describe('The type of ad (RESPONSIVE_SEARCH_AD, etc.)'),
    displayUrl: zod_1.z.string().nullish().describe('The display URL shown in the ad'),
    finalUrls: zod_1.z.array(zod_1.z.string()).nullish().describe('Final URLs for the ad'),
    trackingUrlTemplate: zod_1.z.string().nullish().describe('Tracking URL template'),
    finalUrlSuffix: zod_1.z.string().nullish().describe('Final URL suffix'),
    addedByGoogleAds: zod_1.z
        .boolean()
        .describe('Whether the ad was automatically created by Google Ads'),
}));
// ---------- Combined Action Schema ----------
exports.googleAdsListCampaignsArgsSchema = (0, common_1.GoogleAdsNodeArgsSchema)(zod_1.z.object({
    action: zod_1.z.discriminatedUnion('name', [
        zod_1.z.object({
            name: zod_1.z.literal('listCampaigns'),
            params: listCampaignsParamsSchema,
        }),
        zod_1.z.object({
            name: zod_1.z.literal('listGroups'),
            params: listGroupsParamsSchema,
        }),
        zod_1.z.object({
            name: zod_1.z.literal('listAds'),
            params: listAdsParamsSchema,
        }),
        zod_1.z.object({
            name: zod_1.z.literal('listKeywords'),
            params: listKeywordsParamsSchema,
        }),
    ]),
    emitMode: zod_1.z.literal('multi'),
}));
var GoogleAdsListCampaignsAllResultsSchema = zod_1.z.union([
    listCampaignsResponseSchema,
    listGroupsResponseSchema,
    listAdsResponseSchema,
    listKeywordsResponseSchema,
]);
var googleAdsListCampaignsResultSchemas = (0, common_1.defineDynamicSchemas)()({
    dependencies: ['args.action.name'],
    variants: [
        { when: { 'args.action.name': 'listCampaigns' }, schema: listCampaignsResponseSchema },
        { when: { 'args.action.name': 'listGroups' }, schema: listGroupsResponseSchema },
        { when: { 'args.action.name': 'listAds' }, schema: listAdsResponseSchema },
        { when: { 'args.action.name': 'listKeywords' }, schema: listKeywordsResponseSchema },
    ],
    defaultSchema: GoogleAdsListCampaignsAllResultsSchema,
});
exports.googleAdsListCampaigns = (0, common_1.createToolMetadata)({
    id: 'googleAdsListCampaigns',
    displayName: 'Google Ads List Resources',
    displayDescription: 'List campaigns, groups (ad groups or asset groups), ads, or keywords from Google Ads accounts. CRITICAL: Use action "listGroups" for groups - "listAdGroups" and "listAssetGroups" do NOT exist.',
    category: 'ads',
    color: common_1.BRAND_COLORS.GOOGLE_ADS,
    backgroundColor: (0, common_1.createBgColor)(common_1.BRAND_COLORS.GOOGLE_ADS),
    icon: 'google_ads',
    argsSchema: exports.googleAdsListCampaignsArgsSchema,
}, googleAdsListCampaignsResultSchemas);
